import { useContext, useState } from 'react';

import type { GeneratedKey, Wallet as IWallet } from '@sorare/wallet-shared';
import { MessagingContext } from '@sorare/wallet-shared';
import type { WalletLoaded } from '@sorare/wallet-shared/src/contexts/messaging';
import type { Data } from 'components/Form';
import Form from 'components/Form';
import WalletContext from 'contexts/wallet';
import useCreateWallet from 'hooks/useCreateWallet';
import useHasher from 'hooks/useHasher';
import useInfo from 'hooks/useInfo';
import { DecryptionError } from 'lib/errors';
import { validate } from 'lib/password';
import Wallet from 'lib/wallet';

import type { Props } from './types';

const fields = [['password', 'password']] as const;

type Field = (typeof fields)[number][0];

const GeneratedKeys = ({ onSuccess }: Props) => {
  const { user, dict, fetchUser } = useInfo();
  const createWallet = useCreateWallet();

  const hasher = useHasher();
  const { sendRequest } = useContext(MessagingContext)!;
  const { load } = useContext(WalletContext)!;
  const [hasWrongPasswordError, setHasWrongPasswordError] = useState(false);

  if (!user) return null;

  const { email, nickname, userPrivateKey: key, address, hasPassword } = user;
  const isUpdate = address || key;

  const onSubmit = async ({ password }: Data<Field>) => {
    let data: IWallet;

    if (isUpdate) {
      try {
        const wallet = await Wallet.decryptAsync(password, key!);
        data = await wallet.export(password, email);
      } catch (e) {
        if (e instanceof DecryptionError) {
          return { password: dict.passwordIsInvalid };
        }
        throw e;
      }
    } else {
      const passwordError = await validate(email, nickname, password);
      if (passwordError) return { password: passwordError };

      data = await createWallet(password, email);
    }

    const passwordHash = await hasher(password, email);

    const { error } = await sendRequest<GeneratedKey>('generatedKey', {
      passwordHash,
      wallet: data,
    });

    if (!isUpdate && !error) {
      // Reload user wallet to get the latest userPrivateKey
      await fetchUser();
      await load({
        password,
        userPrivateKey: {
          iv: data.passwordEncryptedPrivateKey.iv,
          encryptedPrivateKey: data.passwordEncryptedPrivateKey.payload,
          salt: data.passwordEncryptedPrivateKey.salt,
        },
      });
    }
    if (error && typeof error === 'object' && 'currentPassword' in error) {
      setHasWrongPasswordError(true);
    }
    if (error) return error;

    await sendRequest<WalletLoaded>('walletLoaded', {});
    return {};
  };

  let title = dict.generateWallet;

  if (isUpdate) {
    title = dict.updateWallet;
  } else if (hasPassword) {
    title = dict.unlockWalletPlaceholder;
  }

  return (
    <Form
      inputs={fields}
      onSubmit={onSubmit}
      onSuccess={onSuccess}
      presetErrors={
        hasWrongPasswordError ? { password: 'passwordIsInvalid' } : undefined
      }
      title={<p className="text-16">{title}</p>}
    />
  );
};

export default GeneratedKeys;
